import React from 'react';
import SortableHeader from '../../components/SortableHeader';
import UiDateField from '../../components/ui/UiDateField';
import {
  Search, Edit2, Trash2, TrendingUp, History, UtensilsCrossed, Calendar, Link2, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import VoiceInput from '../../components/VoiceInput';
import { buildStandardPresetOptions } from '../../utils/dateRangeUi';
import { formatUiDate, formatUiDateTime, formatUiDateTimeRange } from '../../utils/uiFormat';
import { UiSummaryCard, UiSummaryCards } from '../../components/ui/UiSummaryCard';

const INVENTORY_STATES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'perishable', label: 'Perishable' },
  { value: 'suspended', label: 'Suspended' },
];

// Inventory tab — extracted from Inventory.js. Presentational; state/handlers via ctx.
export default function InventoryTab(ctx) {
  const {
    aHandleSort,
    aSortDir,
    aSortKey,
    addCalendarDays,
    adjustData,
    adjustmentHistoryLoading,
    closeInventoryModal,
    departmentFilter,
    departments,
    expiringCount,
    totalItemsCount,
    fetchHistory,
    fetchPurchaseHistory,
    fetchUsedIn,
    filter,
    filteredHistory,
    filteredMaterials,
    formData,
    formatAmount,
    formatDate,
    getCurrentUnitPrice,
    getFiveDayExpiry,
    getLastPurchasedDate,
    hHandleSort,
    hSortDir,
    hSortKey,
    handleAdjust,
    handleApplyInventoryPending,
    handleDelete,
    handleDeleteAdjustmentEntry,
    handleEditAdjustmentEntry,
    handleHistoryPresetChange,
    handleSubmit,
    hasPermission,
    history,
    historyDateRange,
    historyFrom,
    historyPreset,
    historyTo,
    iHandleSort,
    iSortDir,
    iSortKey,
    lowStockCount,
    openAdjustModal,
    openEditModal,
    openPurchaseEntry,
    pHandleSort,
    pSortDir,
    pSortKey,
    pendingInventoryItems,
    purchaseHistory,
    rawMaterials,
    searchQuery,
    selectedItem,
    setAdjustData,
    setDepartmentFilter,
    setFilter,
    setFormData,
    setHistoryFrom,
    setHistoryRangeOverride,
    setHistoryTo,
    setPendingInventoryItems,
    setSearchQuery,
    setShowAdjustModal,
    setShowHistoryModal,
    setShowPurchaseHistoryModal,
    setShowUsedInModal,
    setSupplierFilter,
    shiftHistoryRange,
    showAdjustModal,
    showHistoryModal,
    showModal,
    showPurchaseHistoryModal,
    showUsedInModal,
    sortedAdjustmentHistory,
    sortedHistory,
    sortedPurchaseHistory,
    supplierFilter,
    suppliers,
    totalValue,
    units,
    usedInData,
  } = ctx;
  return (
    <>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {['all', 'low_stock', 'expiring'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-2 rounded-lg font-medium text-sm ${
                  filter === f ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f === 'all' ? 'All' : f === 'low_stock' ? 'Low Stock' : 'Expiring'}
              </button>
            ))}
            <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm">
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm">
              <option value="">All Suppliers</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <UiSummaryCards>
        <UiSummaryCard label="Total Items" value={totalItemsCount} />
        <UiSummaryCard label="Total Value" value={formatAmount(totalValue)} />
        <UiSummaryCard label="Low Stock" value={lowStockCount} tone="text-red-600" onClick={() => setFilter('low_stock')} />
        <UiSummaryCard label="Expiring" value={expiringCount} tone="text-orange-600" onClick={() => setFilter('expiring')} />
      </UiSummaryCards>

      {/* Table - Desktop */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader label="Item" col="name" sortKey={iSortKey} sortDir={iSortDir} onSort={iHandleSort} />
              <SortableHeader label="Quantity" col="quantity" sortKey={iSortKey} sortDir={iSortDir} onSort={iHandleSort} />
              <SortableHeader label="Unit Price" col="purchase_price" sortKey={iSortKey} sortDir={iSortDir} onSort={iHandleSort} />
              <SortableHeader label="Value" col="inventory_value" sortKey={iSortKey} sortDir={iSortDir} onSort={iHandleSort} />
              <SortableHeader label="Status" col="state_display" sortKey={iSortKey} sortDir={iSortDir} onSort={iHandleSort} />
              <SortableHeader label="Expiry" col="expiry_date" sortKey={iSortKey} sortDir={iSortDir} onSort={iHandleSort} />
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredMaterials.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-gray-800">{item.name}</p>
                    <div className="flex items-center justify-between gap-3 text-sm text-gray-500">
                      <span className="text-left">{item.department_name || 'No Department'}</span>
                      <span className="text-right">{item.supplier_name || 'No Supplier'}</span>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={parseFloat(item.quantity) < 0 ? 'text-red-600 font-bold' : item.is_low_stock ? 'text-red-600 font-medium' : ''}>
                    {Math.max(0, parseFloat(item.quantity)).toFixed(2)} {item.unit_abbr}
                  </span>
                </td>
                <td className="px-4 py-3">{formatAmount(getCurrentUnitPrice(item))}</td>
                <td className="px-4 py-3">{formatAmount(Math.max(0, parseFloat(item.quantity) * getCurrentUnitPrice(item)))}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      item.state === 'inactive' ? 'bg-yellow-100 text-yellow-700' :
                      item.state === 'perishable' ? 'bg-orange-100 text-orange-700' :
                      item.state === 'suspended' ? 'bg-gray-200 text-gray-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {item.state_display || item.state || 'Active'}
                    </span>
                    {item.is_low_stock ? (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Low Stock</span>
                    ) : (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">In Stock</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    {item.expiry_date ? (
                      <span className={item.is_expiring_soon ? 'text-orange-600' : ''}>
                        {formatDate(item.expiry_date)}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                    <span className="text-xs text-gray-500">
                      Last Purchased: {getLastPurchasedDate(item) ? formatDate(getLastPurchasedDate(item)) : '—'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => fetchUsedIn(item)} className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Used In Menu Items">
                      <UtensilsCrossed size={16} />
                    </button>
                    <button onClick={() => fetchHistory(item)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="History">
                      <History size={16} />
                    </button>
                    {hasPermission('inventory', 'edit') && (
                      <>
                        <button onClick={() => openAdjustModal(item)} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Adjust Stock">
                          <TrendingUp size={16} />
                        </button>
                        <button onClick={() => fetchPurchaseHistory(item)} className="p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg" title="Purchase Records">
                          <Link2 size={16} />
                        </button>
                        <button onClick={() => openEditModal(item)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                          <Edit2 size={16} />
                        </button>
                      </>
                    )}
                    {hasPermission('inventory', 'delete') && (
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredMaterials.length === 0 && (
          <div className="text-center py-12 text-gray-500">No items found</div>
        )}
      </div>

      {/* Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredMaterials.map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow-sm p-4 border">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold text-gray-800">{item.name}</p>
                <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                  <span className="text-left">{item.department_name || 'No Department'}</span>
                  <span className="text-right">{item.supplier_name || 'No Supplier'}</span>
                </div>
              </div>
              {item.is_low_stock ? (
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.state === 'inactive' ? 'bg-yellow-100 text-yellow-700' :
                    item.state === 'perishable' ? 'bg-orange-100 text-orange-700' :
                    item.state === 'suspended' ? 'bg-gray-200 text-gray-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {item.state_display || item.state || 'Active'}
                  </span>
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Low Stock</span>
                </div>
              ) : (
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.state === 'inactive' ? 'bg-yellow-100 text-yellow-700' :
                    item.state === 'perishable' ? 'bg-orange-100 text-orange-700' :
                    item.state === 'suspended' ? 'bg-gray-200 text-gray-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {item.state_display || item.state || 'Active'}
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">In Stock</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Qty</p>
                <p className={`font-semibold ${parseFloat(item.quantity) < 0 || item.is_low_stock ? 'text-red-600' : ''}`}>{Math.max(0, parseFloat(item.quantity)).toFixed(2)} {item.unit_abbr}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Price</p>
                <p className="font-medium">{formatAmount(getCurrentUnitPrice(item))}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Value</p>
                <p className="font-medium">{formatAmount(Math.max(0, parseFloat(item.quantity) * getCurrentUnitPrice(item)))}</p>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs mb-3">
              {item.expiry_date ? (
                <span className={item.is_expiring_soon ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                  Expiry: {formatDate(item.expiry_date)}
                </span>
              ) : <span className="text-gray-400">Expiry: —</span>}
              <span className="text-gray-500">
                Last Purchased: {getLastPurchasedDate(item) ? formatDate(getLastPurchasedDate(item)) : '—'}
              </span>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <button onClick={() => fetchUsedIn(item)} className="flex-1 py-2 text-xs text-purple-600 bg-purple-50 rounded-lg font-medium flex items-center justify-center gap-1">
                Used In
              </button>
              <button onClick={() => fetchHistory(item)} className="flex-1 py-2 text-xs text-blue-600 bg-blue-50 rounded-lg font-medium flex items-center justify-center gap-1">
                History
              </button>
              {hasPermission('inventory', 'edit') && (
                <>
                  <button onClick={() => openAdjustModal(item)} className="flex-1 py-2 text-xs text-primary-600 bg-primary-50 rounded-lg font-medium flex items-center justify-center gap-1">
                    Adjust
                  </button>
                  <button onClick={() => fetchPurchaseHistory(item)} className="px-3 py-2 text-xs text-orange-600 bg-orange-50 rounded-lg font-medium flex items-center justify-center" title="Purchase Records">
                    <Link2 size={12} />
                  </button>
                  <button onClick={() => openEditModal(item)} className="flex-1 py-2 text-xs text-gray-600 bg-gray-50 rounded-lg font-medium flex items-center justify-center gap-1">
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {filteredMaterials.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-xl">No items found</div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">{selectedItem ? 'Edit Item' : 'Add New Item'}</h3>
              <button onClick={closeInventoryModal} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <VoiceInput type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter item name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit *</label>
                  <select required value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                    <option value="">Select Unit</option>
                    {units.map((u) => (<option key={u.id} value={u.id}>{u.name} ({u.abbreviation})</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Quantity</label>
                  <VoiceInput type="number" step="0.01" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="0" />
                  <p className="text-xs text-gray-400 mt-1">Auto-updated when purchases are recorded</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min. Stock Level</label>
                <VoiceInput type="number" step="0.01" value={formData.min_stock_level} onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })} placeholder="10" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                    <option value="">Select Department</option>
                    {departments.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <select value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                    <option value="">Select Supplier</option>
                    {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                <UiDateField value={formData.expiry_date} onChange={(nextDate) => setFormData({ ...formData, expiry_date: nextDate })} />
                {formData.state === 'perishable' && <p className="text-xs text-gray-400 mt-1">Perishable items default to a 5-day expiry window.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select value={formData.state} onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value, expiry_date: e.target.value === 'perishable' ? (prev.expiry_date || getFiveDayExpiry()) : prev.expiry_date }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500">
                  {INVENTORY_STATES.map(state => <option key={state.value} value={state.value}>{state.label}</option>)}
                </select>
              </div>
              {!selectedItem && (
                <div className="flex justify-end pt-2">
                  <button type="button" onClick={handleApplyInventoryPending} className="px-4 py-2 border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50">Apply</button>
                </div>
              )}
              {!selectedItem && pendingInventoryItems.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending ({pendingInventoryItems.length} items)</p>
                  <div className="border rounded-lg divide-y overflow-hidden">
                    {pendingInventoryItems.map((entry, index) => (
                      <div key={entry._key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-gray-50">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <span className="font-medium text-gray-800 truncate">{entry.label}</span>
                          <span className="text-gray-500">Qty {entry.quantity || 0}</span>
                        </div>
                        <button type="button" onClick={() => setPendingInventoryItems(prev => prev.filter((_, i) => i !== index))} className="p-1 text-gray-400 hover:text-red-600 rounded">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 pt-4">
                <button type="button" onClick={closeInventoryModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">{selectedItem ? 'Save Changes' : `Save All (${pendingInventoryItems.length || 1})`}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {showAdjustModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold">Adjust Stock: {selectedItem.name}</h3>
              <button onClick={() => setShowAdjustModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <p className="text-gray-600">Current Stock: <strong>{selectedItem.quantity} {selectedItem.unit_abbr}</strong></p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Change (+ to add, - to reduce)</label>
                <VoiceInput type="number" step="0.01" value={adjustData.quantity} onChange={(e) => setAdjustData({ ...adjustData, quantity: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <VoiceInput isTextarea value={adjustData.reason} onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })} placeholder="Enter reason for adjustment" rows={3} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowAdjustModal(false); setAdjustData({ id: null, quantity: 0, reason: '', action_type: 'adjustment' }); }} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
                <button onClick={handleAdjust} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">{adjustData.id ? 'Update Adjustment' : 'Save Adjustment'}</button>
              </div>
              <div className="pt-4 border-t space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-base font-semibold text-gray-800">Adjustment History</h4>
                    <p className="text-sm text-gray-500">Manual stock adjustments only</p>
                  </div>
                  {adjustmentHistoryLoading && <span className="text-xs text-gray-400">Loading…</span>}
                </div>
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full hidden sm:table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <SortableHeader label="Date / Time" col="created_at" sortKey={aSortKey} sortDir={aSortDir} onSort={aHandleSort} />
                        <SortableHeader label="Change" col="quantity_change" sortKey={aSortKey} sortDir={aSortDir} onSort={aHandleSort} />
                        <SortableHeader label="Stock After" col="quantity_after" sortKey={aSortKey} sortDir={aSortDir} onSort={aHandleSort} />
                        <SortableHeader label="Reason" col="reason" sortKey={aSortKey} sortDir={aSortDir} onSort={aHandleSort} />
                        <SortableHeader label="User" col="user_name" sortKey={aSortKey} sortDir={aSortDir} onSort={aHandleSort} />
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortedAdjustmentHistory.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">No adjustment records found</td></tr>
                      ) : sortedAdjustmentHistory.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-sm whitespace-nowrap">{formatUiDateTime(entry.created_at)}</td>
                          <td className={`px-4 py-2.5 text-sm font-semibold ${parseFloat(entry.quantity_change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {parseFloat(entry.quantity_change) >= 0 ? '+' : ''}{entry.quantity_change}
                          </td>
                          <td className="px-4 py-2.5 text-sm">{entry.quantity_after}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{entry.reason || '—'}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-500">{entry.user_name || '—'}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => handleEditAdjustmentEntry(entry)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit adjustment">
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => handleDeleteAdjustmentEntry(entry)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete adjustment">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="sm:hidden divide-y">
                    {sortedAdjustmentHistory.length === 0 ? (
                      <p className="px-4 py-10 text-center text-sm text-gray-400">No adjustment records found</p>
                    ) : sortedAdjustmentHistory.map((entry) => (
                      <div key={entry.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{formatUiDateTime(entry.created_at)}</p>
                            <p className="text-xs text-gray-500">{entry.reason || '—'}</p>
                            <p className="text-xs text-gray-500">After: {entry.quantity_after} · {entry.user_name || '—'}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${parseFloat(entry.quantity_change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {parseFloat(entry.quantity_change) >= 0 ? '+' : ''}{entry.quantity_change}
                            </p>
                            <div className="mt-2 flex items-center justify-end gap-1">
                              <button onClick={() => handleEditAdjustmentEntry(entry)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit adjustment">
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => handleDeleteAdjustmentEntry(entry)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete adjustment">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>

            {/* Fixed Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
              <div className="flex items-center gap-3">
                <History className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Usage History</h3>
                  <p className="text-sm text-gray-500">{selectedItem?.name} · Automatic recipe-linked usage only · {history.length} total record{history.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>

            {/* Fixed Filter Bar */}
            <div className="px-5 py-3 border-b bg-gray-50 flex-shrink-0 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="inline-flex items-center rounded-xl border border-gray-200 bg-white shadow-sm">
                  <button type="button" onClick={() => shiftHistoryRange(-1)} className="rounded-l-xl border-r border-gray-200 px-3 py-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600" aria-label="Previous history range">
                    <ChevronLeft size={16} />
                  </button>
                  <select
                    value={historyPreset}
                    onChange={(e) => handleHistoryPresetChange(e.target.value)}
                    className="min-w-[8rem] border-0 bg-transparent px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-0"
                  >
                    {buildStandardPresetOptions(['month', 'year', 'custom']).map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => shiftHistoryRange(1)} className="rounded-r-xl border-l border-gray-200 px-3 py-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600" aria-label="Next history range">
                    <ChevronRight size={16} />
                  </button>
                </div>
                {historyPreset === 'custom' ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <UiDateField value={historyFrom} onChange={(value) => { setHistoryRangeOverride(null); setHistoryFrom(value); }} className="min-w-[15rem]" inputClassName="text-sm" />
                    <span className="text-xs text-gray-400">to</span>
                    <UiDateField value={historyTo} onChange={(value) => { setHistoryRangeOverride(null); setHistoryTo(value); }} className="min-w-[15rem]" inputClassName="text-sm" />
                  </div>
                ) : historyDateRange ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                    <span className="text-gray-500">From</span>
                    <span className="px-3 py-2 border border-gray-200 rounded-lg bg-white">{historyDateRange.from ? formatUiDate(historyDateRange.from) : '—'}</span>
                    <span className="text-gray-500">To</span>
                    <span className="px-3 py-2 border border-gray-200 rounded-lg bg-white">{historyDateRange.to ? formatUiDate(historyDateRange.to) : '—'}</span>
                  </div>
                ) : null}
              </div>
              <div className="text-xs text-gray-500">
                Selected range: {historyDateRange?.from && historyDateRange?.to ? formatUiDateTimeRange(historyDateRange.from, '06:00', addCalendarDays(historyDateRange.to, 1), '05:59') : '—'}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Calendar size={12} className="text-gray-400" />
                <span className="font-medium text-blue-600">{filteredHistory.length} record{filteredHistory.length !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Scrollable Records */}
            <div className="flex-1 overflow-auto">
              {/* Desktop */}
              <table className="w-full hidden sm:table">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <SortableHeader label="Date / Time" col="created_at" sortKey={hSortKey} sortDir={hSortDir} onSort={hHandleSort} />
                    <SortableHeader label="Action" col="action" sortKey={hSortKey} sortDir={hSortDir} onSort={hHandleSort} />
                    <SortableHeader label="Change" col="quantity_change" sortKey={hSortKey} sortDir={hSortDir} onSort={hHandleSort} />
                    <SortableHeader label="Stock After" col="quantity_after" sortKey={hSortKey} sortDir={hSortDir} onSort={hHandleSort} />
                    <SortableHeader label="User" col="user_name" sortKey={hSortKey} sortDir={hSortDir} onSort={hHandleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredHistory.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-sm">No records for this period</td></tr>
                  ) : sortedHistory.map((h) => (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-sm whitespace-nowrap">{formatUiDateTime(h.created_at)}</td>
                      <td className="px-4 py-2.5 text-sm capitalize">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          h.action === 'purchase' ? 'bg-green-100 text-green-700' :
                          h.action === 'sale' ? 'bg-blue-100 text-blue-700' :
                          h.action === 'wastage' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{h.action}</span>
                      </td>
                      <td className={`px-4 py-2.5 text-sm font-semibold ${parseFloat(h.quantity_change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {parseFloat(h.quantity_change) >= 0 ? '+' : ''}{h.quantity_change}
                      </td>
                      <td className="px-4 py-2.5 text-sm">{h.quantity_after}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{h.user_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile */}
              <div className="sm:hidden divide-y">
                {filteredHistory.length === 0 ? (
                  <p className="text-center py-12 text-gray-400 text-sm">No records for this period</p>
                ) : sortedHistory.map((h) => (
                  <div key={h.id} className="px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">{formatUiDateTime(h.created_at)}</span>
                      <span className={`text-xs capitalize px-2 py-0.5 rounded font-medium ${
                        h.action === 'purchase' ? 'bg-green-100 text-green-700' :
                        h.action === 'sale' ? 'bg-blue-100 text-blue-700' :
                        h.action === 'wastage' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{h.action}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-bold ${parseFloat(h.quantity_change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {parseFloat(h.quantity_change) >= 0 ? '+' : ''}{h.quantity_change}
                      </span>
                      <span className="text-xs text-gray-500">After: {h.quantity_after}</span>
                      <span className="text-xs text-gray-500">{h.user_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

      {showPurchaseHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
              <div className="flex items-center gap-3">
                <Link2 className="w-5 h-5 text-orange-600" />
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Purchase Records</h3>
                  <p className="text-sm text-gray-500">{selectedItem?.name} · {purchaseHistory.length} purchase record{purchaseHistory.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <button onClick={() => setShowPurchaseHistoryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-auto">
              <table className="w-full hidden sm:table">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <SortableHeader label="Date" col="date" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                    <SortableHeader label="Expense #" col="expense_number" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                    <SortableHeader label="Qty" col="quantity" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                    <SortableHeader label="Unit" col="unit" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                    <SortableHeader label="Unit Price" col="unit_cost" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} right />
                    <SortableHeader label="Total" col="amount" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} right />
                    <SortableHeader label="Supplier" col="supplier" sortKey={pSortKey} sortDir={pSortDir} onSort={pHandleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {purchaseHistory.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No purchase records found</td></tr>
                  ) : sortedPurchaseHistory.map((row) => (
                    <tr
                      key={`${row.expense_number}-${row.date}-${row.raw_material_id}`}
                      className="hover:bg-orange-50 cursor-pointer"
                      onClick={() => openPurchaseEntry(selectedItem, row)}
                    >
                      <td className="px-4 py-2.5 text-sm whitespace-nowrap">{formatUiDate(row.date)}</td>
                      <td className="px-4 py-2.5 text-sm font-mono text-gray-700">{row.expense_number}</td>
                      <td className="px-4 py-2.5 text-sm">{parseFloat(row.quantity || 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-sm">{row.unit || '-'}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium">{formatAmount(row.unit_cost || 0)}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-semibold text-orange-700">{formatAmount(row.amount || 0)}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">{row.supplier || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="sm:hidden divide-y">
                {purchaseHistory.length === 0 ? (
                  <p className="text-center py-12 text-gray-400 text-sm">No purchase records found</p>
                ) : sortedPurchaseHistory.map((row) => (
                  <button
                    key={`${row.expense_number}-${row.date}-${row.raw_material_id}`}
                    onClick={() => openPurchaseEntry(selectedItem, row)}
                    className="w-full px-4 py-3 text-left hover:bg-orange-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{formatUiDate(row.date)}</p>
                        <p className="text-xs text-gray-500">{row.expense_number}</p>
                        <p className="text-xs text-gray-500">{parseFloat(row.quantity || 0).toLocaleString()} {row.unit || ''} · {row.supplier || '-'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-orange-700">{formatAmount(row.amount || 0)}</p>
                        <p className="text-xs text-gray-500">{formatAmount(row.unit_cost || 0)} / unit</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Used In Modal */}
      {showUsedInModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-4 sm:p-6 max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><UtensilsCrossed size={20} className="text-purple-600" /> Used In</h3>
              <button onClick={() => setShowUsedInModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Ingredient: <strong className="text-gray-800">{usedInData.name}</strong></p>
            {usedInData.items.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <UtensilsCrossed size={32} className="mx-auto mb-2 opacity-50" />
                <p>This inventory item is not used in any menu item or modifier</p>
              </div>
            ) : (
              <div className="space-y-2">
                {usedInData.items.map((mi, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div>
                      <p className="font-medium text-gray-800">{mi.menu_item_name}</p>
                      <p className="text-xs text-gray-500">{mi.usage_label || (mi.usage_type === 'modifier' ? `Modifier in ${mi.menu_item_name}` : `Recipe in ${mi.menu_item_name}`)}</p>
                      <p className="text-xs text-gray-500">{[mi.category, `Uses ${mi.quantity_used} per item`, mi.modifier_option_name ? `Option: ${mi.modifier_option_name}` : ''].filter(Boolean).join(' · ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-700">{formatAmount(mi.selling_price)}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${mi.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {mi.is_available ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-400 text-center pt-2">{usedInData.items.length} usage record(s) found for this inventory item</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
